// Register.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  AcademicCapIcon,
  UserGroupIcon,
  StarIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import SchoolSearchModal from '../components/SchoolSearchModal';
import axios from 'axios';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    gradeLevel: '',
    signupCode: '',
    schoolName: '',
    schoolId: null,  // 새로 추가: School 테이블의 ID
    pendingSchool: null  // 미등록 학교 정보 (회원가입 시 함께 등록)
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 학교 검색 관련 상태
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [registeredSchools, setRegisteredSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [tempToken, setTempToken] = useState('');  // 교사 NEIS 검색용 임시 토큰

  // 등록된 학교 목록 불러오기 (학생용)
  useEffect(() => {
    const fetchRegisteredSchools = async () => {
      setLoadingSchools(true);
      try {
        const response = await axios.get('/api/schools');
        if (response.data.success) {
          setRegisteredSchools(response.data.data || []);
        }
      } catch (error) {
        console.error('등록된 학교 목록 조회 실패:', error);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchRegisteredSchools();
  }, []);

  // 교사 가입코드 확인 시 임시 토큰 발급 (NEIS 검색용)
  useEffect(() => {
    const getTeacherTempToken = async () => {
      if (formData.role === 'teacher' && formData.signupCode === 'wando SW center') {
        try {
          const response = await axios.post('/api/auth/teacher-temp-token', {
            signupCode: formData.signupCode
          });
          if (response.data.token) {
            setTempToken(response.data.token);
          }
        } catch (error) {
          console.error('임시 토큰 발급 실패:', error);
        }
      } else {
        setTempToken('');
      }
    };

    getTeacherTempToken();
  }, [formData.role, formData.signupCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 입력 시 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // 사용자명 검증
    if (!formData.username.trim()) {
      newErrors.username = '사용자명을 입력해주세요.';
    } else if (formData.username.length < 3) {
      newErrors.username = '사용자명은 3자 이상이어야 합니다.';
    }
    
    // 이메일 검증
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    }
    
    // 비밀번호 검증
    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      newErrors.password = '비밀번호는 6자 이상이어야 합니다.';
    }
    
    // 비밀번호 확인 검증
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }
    
    // 역할 검증
    if (!formData.role) {
      newErrors.role = '역할을 선택해주세요.';
    }
    
    // 학년 검증 (학생인 경우)
    if (formData.role === 'student' && !formData.gradeLevel) {
      newErrors.gradeLevel = '학년을 선택해주세요.';
    }
    
    // 가입코드 검증
    if (formData.role === 'teacher') {
      if (!formData.signupCode.trim()) {
        newErrors.signupCode = '교사 가입코드를 입력해주세요.';
      } else if (formData.signupCode.trim() !== 'wando SW center') {
        newErrors.signupCode = '올바른 교사 가입코드를 입력해주세요.';
      }
    } else if (formData.role === 'student' && formData.gradeLevel === 'elementary_high') {
      if (!formData.signupCode.trim()) {
        newErrors.signupCode = '초등 고학년 가입코드를 입력해주세요.';
      } else if (formData.signupCode.trim() !== 'pure ocean') {
        newErrors.signupCode = '올바른 초등 고학년 가입코드를 입력해주세요.';
      }
    }
    
    // 학교 검증
    if (!formData.schoolName.trim()) {
      newErrors.schoolName = '학교를 선택해주세요.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setMessage('');
    
    try {
      const response = await axios.post('/api/auth/register', formData);
      
      if (response.status === 201) {
        setMessage('회원가입이 완료되었습니다! 로그인 페이지로 이동합니다.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      } else {
        setErrors({ general: '회원가입 중 오류가 발생했습니다. 다시 시도해주세요.' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <LoadingSpinner size="lg" message="회원가입 중..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* 헤더 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <motion.div
            whileHover={{ rotate: 10 }}
            className="mx-auto h-16 w-16 bg-primary-500 rounded-full flex items-center justify-center mb-4"
          >
            <ShieldCheckIcon className="h-10 w-10 text-white" />
          </motion.div>
          <h2 className="text-3xl font-bold gradient-text">
            SafeAI Kids 회원가입
          </h2>
          <p className="mt-2 text-gray-600">
            완도교육지원청/Upstage 지원 플랫폼
          </p>
        </motion.div>

        {/* 회원가입 폼 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* 성공 메시지 */}
            {message && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4"
              >
                <p className="text-sm text-green-600">{message}</p>
              </motion.div>
            )}

            {/* 전체 에러 메시지 */}
            {errors.general && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <p className="text-sm text-red-600">{errors.general}</p>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 사용자명 */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  사용자명 *
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  className={`input-field ${errors.username ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="사용자명을 입력하세요"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                )}
              </div>

              {/* 이메일 */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일 *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="이메일을 입력하세요"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 비밀번호 */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호 *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className={`input-field pr-12 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="비밀번호를 입력하세요"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* 비밀번호 확인 */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호 확인 *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`input-field pr-12 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="비밀번호를 다시 입력하세요"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 역할 선택 */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  역할 *
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={`input-field ${errors.role ? 'border-red-500 focus:ring-red-500' : ''}`}
                >
                  <option value="">역할을 선택하세요</option>
                  <option value="student">학생</option>
                  <option value="teacher">교사</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                )}
              </div>

              {/* 학년 선택 (학생인 경우만) */}
              {formData.role === 'student' && (
                <div>
                  <label htmlFor="gradeLevel" className="block text-sm font-medium text-gray-700 mb-2">
                    학년 *
                  </label>
                  <select
                    id="gradeLevel"
                    name="gradeLevel"
                    value={formData.gradeLevel}
                    onChange={handleChange}
                    className={`input-field ${errors.gradeLevel ? 'border-red-500 focus:ring-red-500' : ''}`}
                  >
                    <option value="">학년을 선택하세요</option>
                    <option value="elementary_low">초등 저학년 (1-3학년)</option>
                    <option value="elementary_high">초등 고학년 (4-6학년)</option>
                  </select>
                  {errors.gradeLevel && (
                    <p className="mt-1 text-sm text-red-600">{errors.gradeLevel}</p>
                  )}
                </div>
              )}
            </div>

            {/* 가입코드 (교사 또는 초등 고학년인 경우) */}
            {((formData.role === 'teacher') || (formData.role === 'student' && formData.gradeLevel === 'elementary_high')) && (
              <div>
                <label htmlFor="signupCode" className="block text-sm font-medium text-gray-700 mb-2">
                  가입코드 *
                </label>
                <input
                  id="signupCode"
                  name="signupCode"
                  type="text"
                  value={formData.signupCode}
                  onChange={handleChange}
                  className={`input-field ${errors.signupCode ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder={
                    formData.role === 'teacher' 
                      ? '교사 가입코드를 입력하세요' 
                      : '초등 고학년 가입코드를 입력하세요'
                  }
                />
                {errors.signupCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.signupCode}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.role === 'teacher' 
                    ? '교사 가입코드는 교육센터에서 제공받으실 수 있습니다.' 
                    : '초등 고학년 가입코드는 담당 교사에게 문의하세요.'}
                </p>
              </div>
            )}

            {/* 학교 선택 - 교사는 NEIS 검색, 학생은 등록된 학교 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                학교 *
              </label>

              {/* 교사: NEIS 학교 검색 버튼 */}
              {formData.role === 'teacher' ? (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.schoolName}
                      readOnly
                      placeholder="학교를 검색하여 선택하세요"
                      className={`input-field flex-1 bg-gray-50 ${errors.schoolName ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSchoolModal(true)}
                      disabled={!tempToken}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <MagnifyingGlassIcon className="w-5 h-5" />
                      학교 검색
                    </button>
                  </div>
                  {!tempToken && formData.role === 'teacher' && (
                    <p className="mt-1 text-xs text-gray-500">
                      * 올바른 교사 가입코드를 입력하면 학교 검색이 활성화됩니다.
                    </p>
                  )}
                  {formData.schoolId && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <BuildingOfficeIcon className="w-4 h-4" />
                      {formData.schoolName} (NEIS 등록됨)
                    </p>
                  )}
                  {formData.pendingSchool && !formData.schoolId && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 font-medium flex items-center gap-1">
                        <BuildingOfficeIcon className="w-4 h-4" />
                        {formData.schoolName}
                      </p>
                      {formData.pendingSchool.address && (
                        <p className="text-xs text-blue-600 mt-1 ml-5">
                          📍 {formData.pendingSchool.address}
                        </p>
                      )}
                      <p className="text-xs text-blue-500 mt-1 ml-5">
                        ✓ 회원가입 완료 시 자동 등록됩니다
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* 학생: 등록된 학교 선택 드롭다운 */
                <div>
                  {loadingSchools ? (
                    <div className="flex items-center gap-2 py-2 text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span>학교 목록을 불러오는 중...</span>
                    </div>
                  ) : registeredSchools.length > 0 ? (
                    <select
                      id="schoolName"
                      name="schoolName"
                      value={formData.schoolId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedSchool = registeredSchools.find(s => s.id === selectedId);
                        setFormData(prev => ({
                          ...prev,
                          schoolId: selectedId,
                          schoolName: selectedSchool ? selectedSchool.name : ''
                        }));
                        if (errors.schoolName) {
                          setErrors(prev => ({ ...prev, schoolName: '' }));
                        }
                      }}
                      className={`input-field ${errors.schoolName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">학교를 선택하세요</option>
                      {registeredSchools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name} {school.address ? `(${school.address.split(' ')[0]})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        아직 등록된 학교가 없습니다. 담당 교사에게 문의해주세요.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {errors.schoolName && (
                <p className="mt-1 text-sm text-red-600">{errors.schoolName}</p>
              )}
            </div>

            {/* 회원가입 버튼 */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg"
            >
              {loading ? '회원가입 중...' : '회원가입'}
            </motion.button>

            {/* 로그인 링크 */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                이미 계정이 있으신가요?{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  로그인
                </Link>
              </p>
            </div>
          </form>
        </motion.div>

        {/* 가입 안내 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-white rounded-xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            📋 가입 안내
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-blue-500 mt-1" />
              <div>
                <p className="font-medium text-blue-900">초등 저학년</p>
                <p className="text-sm text-blue-700">가입코드 불필요</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
              <StarIcon className="h-6 w-6 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">초등 고학년</p>
                <p className="text-sm text-green-700">가입코드 필요</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-purple-500 mt-1" />
              <div>
                <p className="font-medium text-purple-900">교사</p>
                <p className="text-sm text-purple-700">교사 가입코드 필요</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 안전 보장 메시지 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 text-center"
        >
          <div className="flex items-center justify-center space-x-2 text-safe-600">
            <ShieldCheckIcon className="h-5 w-5" />
            <span className="text-sm font-medium">
              모든 개인정보는 안전하게 보호됩니다
            </span>
          </div>
        </motion.div>
      </div>

      {/* NEIS 학교 검색 모달 (교사용) */}
      <SchoolSearchModal
        isOpen={showSchoolModal}
        onClose={() => setShowSchoolModal(false)}
        onSelectSchool={(school) => {
          if (school.pendingRegistration) {
            // 회원가입 중 - 학교 정보 임시 저장 (회원가입 완료 시 등록)
            setFormData(prev => ({
              ...prev,
              schoolName: school.name,
              schoolId: null,
              pendingSchool: {
                neisCode: school.neisCode,
                officeCode: school.officeCode,
                name: school.name,
                engName: school.engName,
                schoolType: school.schoolType,
                address: school.address
              }
            }));
          } else {
            // 이미 등록된 학교 선택
            setFormData(prev => ({
              ...prev,
              schoolName: school.name,
              schoolId: school.id,
              pendingSchool: null
            }));
          }
          if (errors.schoolName) {
            setErrors(prev => ({ ...prev, schoolName: '' }));
          }
        }}
        token={tempToken}
      />
    </div>
  );
};

export default Register;
